<template>
  <!-- marker_config from the backend (non-empty): renders the same pin the map itself would use,
       taking precedence over the style-derived swatch below. -->
  <img
    v-if="markerConfigImgUrl"
    :src="markerConfigImgUrl"
    :width="MARKER_CONFIG_SWATCH_SIZE"
    :height="MARKER_CONFIG_SWATCH_SIZE"
    class="LayerStyleSwatch LayerStyleSwatch--img LayerStyleSwatch--markerConfig"
    aria-hidden="true"
  />

  <!-- Custom uploaded icon: render as <img>, bypassing the MapLibre sprite registry -->
  <img
    v-else-if="customIconUrl"
    :src="resolvedCustomIconUrl"
    :width="SWATCH_SIZE"
    :height="SWATCH_SIZE"
    class="LayerStyleSwatch LayerStyleSwatch--img"
    aria-hidden="true"
  />

  <!-- Polygon -->
  <svg
    v-else-if="geometryType === 'polygon'"
    viewBox="0 0 20 20"
    width="20"
    height="20"
    xmlns="http://www.w3.org/2000/svg"
    class="LayerStyleSwatch"
    aria-hidden="true"
  >
    <rect
      x="1.5" y="1.5" width="17" height="17" rx="2"
      :fill="fillColor"
      :fill-opacity="fillOpacity"
      :stroke="outlineColor"
      stroke-width="1.5"
    />
  </svg>

  <!-- Line / Roads -->
  <svg
    v-else-if="!customIconUrl && (geometryType === 'line' || geometryType === 'roads')"
    viewBox="0 0 20 20"
    width="20"
    height="20"
    xmlns="http://www.w3.org/2000/svg"
    class="LayerStyleSwatch"
    aria-hidden="true"
  >
    <polyline
      points="2,16 8,8 13,12 18,4"
      fill="none"
      :stroke="lineColor"
      :stroke-width="clampedLineWidth"
      :stroke-dasharray="dashArray"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>

  <!-- Point: canvas renders the exact MapLibre sprite icon -->
  <canvas
    v-else-if="hasSpriteIcon"
    ref="canvasRef"
    :width="SWATCH_SIZE"
    :height="SWATCH_SIZE"
    class="LayerStyleSwatch"
    aria-hidden="true"
    style="image-rendering: pixelated"
  />

  <!-- Fallback: generic dot -->
  <svg
    v-else
    viewBox="0 0 20 20"
    width="20"
    height="20"
    xmlns="http://www.w3.org/2000/svg"
    class="LayerStyleSwatch"
    aria-hidden="true"
  >
    <circle cx="10" cy="10" r="5" :fill="markerColor" />
    <circle cx="10" cy="10" r="2" fill="white" fill-opacity="0.75" />
  </svg>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { LayerStyle } from '@/models/interfaces/map/Layer'
import type { TypeOuvrageMarkerConfig } from '@/models/interfaces/map/MarkerConfig'
import { useMyMapStore } from '@/stores/myMapStore'
import { resolveIconLoadUrl } from '@/services/map/mapIconCatalog'
import { MarkerGeneratorService } from '@/services/map/MarkerGeneratorService'
import {
  isMarkerConfigEmpty,
  resolveEffectiveConfig,
  resolveIconBinaryUrl,
} from '@/services/map/TypeOuvrageMarkerService'

const SWATCH_SIZE = 20
/** Bigger than SWATCH_SIZE: marker_config icons are self-contained badges meant to read clearly,
 *  not a small dot/sprite swatch — shrinking them to SWATCH_SIZE made the picto illegible. */
const MARKER_CONFIG_SWATCH_SIZE = 28

const FALLBACK_FILL = '#94a3b8'
const FALLBACK_OUTLINE = '#475569'
const FALLBACK_LINE = '#3b82f6'
const FALLBACK_MARKER = '#64748b'

const props = defineProps<{
  layerStyle?: LayerStyle
}>()

const mapStore = useMyMapStore()
const canvasRef = ref<HTMLCanvasElement | null>(null)
const hasSpriteIcon = ref(false)

const geometryType = computed(() => props.layerStyle?.geometryType ?? 'point')
const paint = computed(() => props.layerStyle?.paint ?? {})
const rawIconName = computed(() => props.layerStyle?.iconName ?? null)

/** Resolved URL for a custom uploaded icon (handles dev proxy rewrite and session blob cache). */
const customIconUrl = computed(() => props.layerStyle?.customIconUrl ?? null)
const resolvedCustomIconUrl = computed(() =>
  customIconUrl.value ? resolveIconLoadUrl(customIconUrl.value) : '',
)

/**
 * Per type d'ouvrage / donnée marker_config from the backend. When non-empty, it takes precedence
 * over the style-derived swatch below; when empty/absent, the current rendering is kept untouched.
 */
const markerConfig = computed<TypeOuvrageMarkerConfig | null>(
  () => (props.layerStyle?.markerConfig as TypeOuvrageMarkerConfig | null | undefined) ?? null,
)
const hasMarkerConfig = computed(() => !isMarkerConfigEmpty(markerConfig.value))
const markerConfigImgUrl = ref<string | null>(null)

async function buildMarkerConfigSwatch(): Promise<void> {
  if (!hasMarkerConfig.value) {
    markerConfigImgUrl.value = null
    return
  }

  const config = markerConfig.value
  const resolved = resolveEffectiveConfig('', config, null)

  try {
    if (resolved.source === 'url' && resolved.iconUrl) {
      const absoluteUrl = await resolveIconBinaryUrl(resolved.iconUrl)
      const isSvg =
        absoluteUrl.toLowerCase().includes('.svg') || resolved.iconUrl.toLowerCase().includes('.svg')
      if (isSvg) {
        const svgText = await MarkerGeneratorService.fetchSvgFromUrl(absoluteUrl)
        // These backend-provided icons are self-contained badges (own background + picto) — render
        // as-is at a bigger size instead of shrinking them, tinted white, inside our own pin.
        const markerSvg = MarkerGeneratorService.createStandaloneIconMarker(svgText, MARKER_CONFIG_SWATCH_SIZE * 2)
        markerConfigImgUrl.value = MarkerGeneratorService.svgToDataUrl(markerSvg)
      } else {
        markerConfigImgUrl.value = absoluteUrl
      }
      return
    }

    // lucideIcon, sprite (no dedicated rendering, falls back like on the map itself) or color-only.
    const markerSvg = await MarkerGeneratorService.generateMarker(
      resolved.markerColor,
      resolved.source === 'lucide' ? resolved.lucideIcon : undefined,
      resolved.iconColor,
      false,
    )
    markerConfigImgUrl.value = MarkerGeneratorService.svgToDataUrl(markerSvg)
  } catch (err) {
    console.warn('[LayerStyleSwatch] failed to build marker_config swatch', err)
    markerConfigImgUrl.value = null
  }
}

watch(markerConfig, () => void buildMarkerConfigSwatch(), { immediate: true })

/**
 * Recursively extract a usable CSS color string from a MapLibre paint value.
 * Handles plain strings, rgba/rgb arrays, expression arrays, and stops objects.
 */
function extractColor(value: unknown): string | null {
  if (!value) return null
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    if (value[0] === 'rgba' && value.length >= 5) {
      return `rgba(${value[1]},${value[2]},${value[3]},${value[4]})`
    }
    if (value[0] === 'rgb' && value.length >= 4) {
      return `rgb(${value[1]},${value[2]},${value[3]})`
    }
    for (const item of value) {
      const found = extractColor(item)
      if (found) return found
    }
  }
  if (typeof value === 'object' && value !== null) {
    const stops = (value as Record<string, unknown>).stops
    if (Array.isArray(stops) && stops.length > 0) {
      const firstStop = stops[0]
      if (Array.isArray(firstStop) && firstStop.length >= 2) {
        return extractColor(firstStop[1])
      }
    }
  }
  return null
}

function extractNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number') return value
  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === 'number') return item
      if (Array.isArray(item) && item.length >= 2 && typeof item[1] === 'number') return item[1]
    }
  }
  if (typeof value === 'object' && value !== null) {
    const stops = (value as Record<string, unknown>).stops
    if (Array.isArray(stops) && stops.length > 0) {
      const first = stops[0]
      if (Array.isArray(first) && typeof first[1] === 'number') return first[1]
    }
  }
  return fallback
}

// --- Polygon ---
const fillColor = computed(() => extractColor(paint.value['fill-color']) ?? FALLBACK_FILL)
const fillOpacity = computed(() => extractNumber(paint.value['fill-opacity'], 0.6))
const outlineColor = computed(
  () =>
    extractColor(paint.value['fill-outline-color']) ??
    extractColor(paint.value['line-color']) ??
    FALLBACK_OUTLINE,
)

// --- Line ---
const lineColor = computed(() => extractColor(paint.value['line-color']) ?? FALLBACK_LINE)
const rawLineWidth = computed(() => extractNumber(paint.value['line-width'], 2))
const clampedLineWidth = computed(() => Math.min(Math.max(rawLineWidth.value, 1.5), 4))
const dashArray = computed(() => {
  const da = paint.value['line-dasharray']
  if (!Array.isArray(da) || da.length === 0) return undefined
  const max = Math.max(...da.map(Number))
  if (max === 0) return undefined
  const scale = 4 / max
  return da.map((v: number) => v * scale).join(' ')
})

// --- Point fallback color ---
const markerColor = computed(
  () =>
    extractColor(paint.value['circle-color']) ??
    extractColor(paint.value['icon-color']) ??
    FALLBACK_MARKER,
)

// --- Sprite icon rendering ---
function drawSpriteIcon(): void {
  const map = mapStore.map
  const iconName = rawIconName.value
  if (!map || !iconName) {
    hasSpriteIcon.value = false
    return
  }

  // getImage is available on the MapLibre Map instance
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const img = (map as any).getImage(iconName)
  if (!img || !img.data) {
    hasSpriteIcon.value = false
    return
  }

  hasSpriteIcon.value = true

  nextTick(() => {
    const canvas = canvasRef.value
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const { data, width, height } = img.data as { data: Uint8Array; width: number; height: number }

    const clamped = new Uint8ClampedArray(data.length)
    clamped.set(data)
    const imageData = new ImageData(clamped, width, height)
    const tmpCanvas = document.createElement('canvas')
    tmpCanvas.width = width
    tmpCanvas.height = height
    const tmpCtx = tmpCanvas.getContext('2d')
    if (!tmpCtx) return
    tmpCtx.putImageData(imageData, 0, 0)

    ctx.clearRect(0, 0, SWATCH_SIZE, SWATCH_SIZE)
    ctx.drawImage(tmpCanvas, 0, 0, SWATCH_SIZE, SWATCH_SIZE)
  })
}

// Watch for map availability and icon name changes
watch(
  [() => mapStore.map, rawIconName],
  () => {
    if (geometryType.value === 'point') drawSpriteIcon()
  },
  { immediate: true },
)

// If the icon image was not yet registered at render time (e.g. custom uploaded icons
// loaded asynchronously after layer application), retry once the map reaches idle state.
watch(
  [() => mapStore.map, rawIconName, hasSpriteIcon],
  ([map, iconName, hasIcon]) => {
    if (geometryType.value !== 'point' || hasIcon || !map || !iconName) return
    const handler = () => {
      drawSpriteIcon()
    }
    map.once('idle', handler)
  },
  { immediate: true },
)
</script>

<style>
.LayerStyleSwatch {
  flex-shrink: 0;
  display: block;
}

.LayerStyleSwatch--img {
  object-fit: contain;
  border-radius: 2px;
}
</style>
